import { useMemo, useState } from 'react';
import {
  BarChart3,
  Car,
  Download,
  FileSpreadsheet,
  Fuel,
  Printer,
  ShieldAlert,
  Wrench,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { formatBRL, formatDate } from '@/lib/format';
import { useVehiclesList } from '@/features/vehicles/api';
import { useFinancialClosing, type VehicleRow } from './api';

// --- Period helpers --------------------------------------------------------

function toInputDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function presetCurrentMonth() {
  const now = new Date();
  return {
    from: toInputDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: toInputDate(now),
  };
}
function presetPreviousMonth() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const to = new Date(now.getFullYear(), now.getMonth(), 0);
  return { from: toInputDate(from), to: toInputDate(to) };
}
function presetCurrentQuarter() {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3);
  const from = new Date(now.getFullYear(), quarter * 3, 1);
  return { from: toInputDate(from), to: toInputDate(now) };
}
function presetCurrentYear() {
  const now = new Date();
  return {
    from: toInputDate(new Date(now.getFullYear(), 0, 1)),
    to: toInputDate(now),
  };
}

type Preset = 'current_month' | 'previous_month' | 'quarter' | 'year' | 'custom';

// --- Exporters -------------------------------------------------------------

interface ExportRow {
  Placa: string;
  Veículo: string;
  Status: string;
  'Abastecimento (R$)': number;
  'Litros': number;
  'Manutenção (R$)': number;
  'OS concluídas': number;
  'Multas pagas (R$)': number;
  'Nº multas': number;
  'TOTAL (R$)': number;
}

function rowsToExport(rows: VehicleRow[]): ExportRow[] {
  return rows.map((r) => ({
    Placa: r.plate,
    Veículo: [r.brand, r.model].filter(Boolean).join(' '),
    Status: r.active ? 'Ativo' : 'Inativo',
    'Abastecimento (R$)': r.fuelingCents / 100,
    'Litros': r.fuelingLiters,
    'Manutenção (R$)': r.maintenanceCents / 100,
    'OS concluídas': r.maintenanceCount,
    'Multas pagas (R$)': r.finesCents / 100,
    'Nº multas': r.finesCount,
    'TOTAL (R$)': r.totalCents / 100,
  }));
}

function downloadCSV(rows: VehicleRow[], filename: string) {
  const data = rowsToExport(rows);
  if (data.length === 0) return;
  const headers = Object.keys(data[0]);
  const escape = (v: string | number) => {
    const s = String(v);
    if (s.includes(';') || s.includes('"') || s.includes('\n')) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };
  const formatNumber = (n: number) =>
    n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const lines = [
    headers.join(';'),
    ...data.map((row) =>
      headers
        .map((h) => {
          const val = row[h as keyof ExportRow];
          if (typeof val === 'number' && h !== 'Litros' && !h.includes('Nº') && !h.includes('OS')) {
            return escape(formatNumber(val));
          }
          if (typeof val === 'number' && h === 'Litros') {
            return escape(val.toLocaleString('pt-BR', { minimumFractionDigits: 2 }));
          }
          return escape(val);
        })
        .join(';'),
    ),
  ];
  const blob = new Blob(['\ufeff' + lines.join('\n')], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function downloadXLSX(rows: VehicleRow[], filename: string, totals?: VehicleRow) {
  const data = rowsToExport(rows);
  if (totals) {
    data.push({
      Placa: 'TOTAL',
      Veículo: '',
      Status: '',
      'Abastecimento (R$)': totals.fuelingCents / 100,
      'Litros': totals.fuelingLiters,
      'Manutenção (R$)': totals.maintenanceCents / 100,
      'OS concluídas': totals.maintenanceCount,
      'Multas pagas (R$)': totals.finesCents / 100,
      'Nº multas': totals.finesCount,
      'TOTAL (R$)': totals.totalCents / 100,
    });
  }
  const ws = XLSX.utils.json_to_sheet(data);
  // Larguras das colunas
  ws['!cols'] = [
    { wch: 12 }, { wch: 28 }, { wch: 8 }, { wch: 18 }, { wch: 10 },
    { wch: 18 }, { wch: 14 }, { wch: 18 }, { wch: 10 }, { wch: 16 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Fechamento');
  XLSX.writeFile(wb, filename);
}

// --- Page ------------------------------------------------------------------

export function ReportsPage() {
  const [preset, setPreset] = useState<Preset>('current_month');
  const [{ from, to }, setRange] = useState(presetCurrentMonth);
  const [vehicleId, setVehicleId] = useState<string>('all');

  const vehiclesQuery = useVehiclesList({ limit: 100, sort: 'plate' });
  const closingQuery = useFinancialClosing({
    fromDate: from || undefined,
    toDate: to || undefined,
    vehicleId: vehicleId === 'all' ? undefined : vehicleId,
  });

  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (p === 'current_month') setRange(presetCurrentMonth());
    else if (p === 'previous_month') setRange(presetPreviousMonth());
    else if (p === 'quarter') setRange(presetCurrentQuarter());
    else if (p === 'year') setRange(presetCurrentYear());
  };

  const closing = closingQuery.data;
  const rows = useMemo(
    () => closing?.byVehicle.filter((r) => r.totalCents > 0) ?? [],
    [closing],
  );
  const emptyRows = (closing?.byVehicle.length ?? 0) - rows.length;
  const totals = closing?.totals;

  const filename = useMemo(() => {
    return `frota-fechamento-${from}-a-${to}`;
  }, [from, to]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="page" data-print-region>
      <header className="flex flex-col gap-4 print:hidden md:flex-row md:items-start md:justify-between">
        <div className="space-y-1.5">
          <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <BarChart3 className="size-3.5" /> Administração
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">Relatórios</h1>
          <p className="text-sm text-muted-foreground">
            Fechamento financeiro do período — abastecimento, manutenção e multas por veículo.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={handlePrint} disabled={!closing}>
            <Printer className="size-4" />
            Imprimir
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadCSV(rows, `${filename}.csv`)}
            disabled={rows.length === 0}
          >
            <Download className="size-4" />
            CSV
          </Button>
          <Button
            size="sm"
            onClick={() =>
              downloadXLSX(
                rows,
                `${filename}.xlsx`,
                totals
                  ? ({
                      vehicleId: '',
                      plate: 'TOTAL',
                      brand: null,
                      model: null,
                      active: true,
                      ...totals,
                    } as VehicleRow)
                  : undefined,
              )
            }
            disabled={rows.length === 0}
          >
            <FileSpreadsheet className="size-4" />
            Excel
          </Button>
        </div>
      </header>

      {/* Cabeçalho de impressão */}
      <div className="hidden print:mb-6 print:block">
        <h1 className="text-2xl font-semibold">Fechamento financeiro</h1>
        <p className="text-sm text-muted-foreground">
          Período: {formatDate(from)} a {formatDate(to)}
        </p>
      </div>

      {/* Filtros */}
      <Card className="print:hidden">
        <CardHeader>
          <CardTitle className="text-base">Período e filtros</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2 sm:grid-cols-5">
            {[
              { key: 'current_month' as Preset, label: 'Mês atual' },
              { key: 'previous_month' as Preset, label: 'Mês anterior' },
              { key: 'quarter' as Preset, label: 'Trimestre atual' },
              { key: 'year' as Preset, label: 'Ano atual' },
              { key: 'custom' as Preset, label: 'Personalizado' },
            ].map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => applyPreset(p.key)}
                className={`rounded-md border px-3 py-2 text-xs font-medium transition-colors ${
                  preset === p.key
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border hover:bg-accent/40'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="from">Data inicial</Label>
              <Input
                id="from"
                type="date"
                value={from}
                onChange={(e) => {
                  setPreset('custom');
                  setRange((r) => ({ ...r, from: e.target.value }));
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="to">Data final</Label>
              <Input
                id="to"
                type="date"
                value={to}
                onChange={(e) => {
                  setPreset('custom');
                  setRange((r) => ({ ...r, to: e.target.value }));
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="vehicle">Veículo</Label>
              <Select value={vehicleId} onValueChange={setVehicleId}>
                <SelectTrigger id="vehicle">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os veículos</SelectItem>
                  {(vehiclesQuery.data?.data ?? []).map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.plate}
                      {(v.brand || v.model) && ` — ${[v.brand, v.model].filter(Boolean).join(' ')}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      {closingQuery.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : (
        totals && (
          <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              icon={Fuel}
              title="Abastecimento"
              value={formatBRL(totals.fuelingCents)}
              hint={`${totals.fuelingCount} registros · ${totals.fuelingLiters.toLocaleString('pt-BR')} L`}
              tone="primary"
            />
            <KpiCard
              icon={Wrench}
              title="Manutenção"
              value={formatBRL(totals.maintenanceCents)}
              hint={`${totals.maintenanceCount} OS concluídas`}
              tone="warning"
            />
            <KpiCard
              icon={ShieldAlert}
              title="Multas pagas"
              value={formatBRL(totals.finesCents)}
              hint={`${totals.finesCount} multas`}
              tone="danger"
            />
            <KpiCard
              icon={BarChart3}
              title="Total no período"
              value={formatBRL(totals.totalCents)}
              hint={`${rows.length} veículo${rows.length === 1 ? '' : 's'} com movimento`}
              tone="success"
            />
          </section>
        )
      )}

      {/* Breakdown por veículo */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Por veículo</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {closingQuery.isLoading ? (
            <div className="space-y-3 p-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <Car className="size-10 text-muted-foreground" />
              <p className="text-sm font-medium">Sem movimento no período</p>
              <p className="text-xs text-muted-foreground">
                Nenhum veículo registrou abastecimento, manutenção ou multa entre{' '}
                {formatDate(from)} e {formatDate(to)}.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Veículo</th>
                    <th className="px-4 py-3 text-right font-medium">Abastecimento</th>
                    <th className="px-4 py-3 text-right font-medium">Manutenção</th>
                    <th className="px-4 py-3 text-right font-medium">Multas</th>
                    <th className="px-4 py-3 text-right font-medium">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((r) => (
                    <tr key={r.vehicleId} className="transition-colors hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-2">
                          <Car className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                          <div>
                            <p className="font-mono text-xs font-semibold">{r.plate}</p>
                            {(r.brand || r.model) && (
                              <p className="text-[11px] text-muted-foreground">
                                {[r.brand, r.model].filter(Boolean).join(' ')}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <p className="font-mono text-xs font-medium tabular-nums">
                          {formatBRL(r.fuelingCents)}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {r.fuelingCount} reg · {r.fuelingLiters.toLocaleString('pt-BR')} L
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <p className="font-mono text-xs font-medium tabular-nums">
                          {formatBRL(r.maintenanceCents)}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {r.maintenanceCount} OS
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <p className="font-mono text-xs font-medium tabular-nums">
                          {formatBRL(r.finesCents)}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {r.finesCount} multa{r.finesCount === 1 ? '' : 's'}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <p className="font-mono text-sm font-semibold tabular-nums">
                          {formatBRL(r.totalCents)}
                        </p>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {totals && (
                  <tfoot>
                    <tr className="border-t-2 border-border bg-muted/30 text-sm font-semibold">
                      <td className="px-4 py-3">TOTAL</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">
                        {formatBRL(totals.fuelingCents)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">
                        {formatBRL(totals.maintenanceCents)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">
                        {formatBRL(totals.finesCents)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-base tabular-nums">
                        {formatBRL(totals.totalCents)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
              {emptyRows > 0 && (
                <p className="border-t border-border bg-muted/20 p-3 text-center text-xs text-muted-foreground print:hidden">
                  + {emptyRows} veícul{emptyRows === 1 ? 'o sem' : 'os sem'} movimento no período (ocultos)
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface KpiCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  value: string;
  hint?: string;
  tone: 'primary' | 'success' | 'warning' | 'danger';
}

function KpiCard({ icon: Icon, title, value, hint, tone }: KpiCardProps) {
  const toneClasses = {
    primary: 'bg-primary/10 text-primary',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/15 text-warning-foreground',
    danger: 'bg-destructive/10 text-destructive',
  }[tone];
  return (
    <Card>
      <CardContent className="flex items-start justify-between p-5">
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {title}
          </p>
          <p className="font-mono text-2xl font-semibold tracking-tight tabular-nums">
            {value}
          </p>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
        <div className={`flex size-10 items-center justify-center rounded-lg ${toneClasses}`}>
          <Icon className="size-5" />
        </div>
      </CardContent>
    </Card>
  );
}

